import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  Input,
  NgZone,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IPageInfo } from '@iharbeck/ngx-virtual-scroller';
import { debounceTime, distinctUntilChanged, filter, firstValueFrom, map, NEVER, Observable, shareReplay, skip, switchMap } from 'rxjs';
import { PagedGrid } from '../../models';
import { ColumnData, ExpandCollapseRow, SubRowType } from '../../models/grid-chunk.models';
import { PagedGridAnalytics, PagedGridFacade } from '../../services';

interface CalculatedColumnData {
  heightPx: number;
  widthPx: number;
  positionPx: number;
}

interface CalculatedTableData {
  columns: CalculatedColumnData[];
  widthPx: number;
}

interface SubrowContext {
  index: number;
  rowId: string;
  rowIndex: number;
  subRowIndex: number;
  evenRow: boolean,
  oddRow: boolean,
  isFirstRow: boolean;
  isLastRow: boolean;
  isFirstSubrowInRow: boolean,
  isMiddleSubrowInRow: boolean,
  isLastSubrowInRow: boolean
}

interface CellContext extends SubrowContext {
  isFirstCell: boolean;
  isLastCell: boolean;
  columnId: string;
  cellValue: any;
  columnIndex: number;
}

@Component({
  selector: 'app-grid-v2',
  templateUrl: './grid-v2.component.html',
  styleUrls: ['./grid-v2.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GridV2Component implements OnInit, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly cd = inject(ChangeDetectorRef);
  private readonly pagedGridFacade = inject(PagedGridFacade);
  private readonly pagedGridAnalyticsFacade = inject(PagedGridAnalytics);
  private readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly ngZone = inject(NgZone);
  private currentViewId: string;
  private hostWidthChange$: Observable<ResizeObserverEntry>;
  private isHostVisible$: Observable<boolean>;
  protected sampleSubRows: PagedGrid.BaseGridChunkSubRowMetadata[];
  protected gridModel: PagedGrid.ComputedGridModel;

  protected sortInfo$: Observable<any>;
  protected calculatedTableData: CalculatedTableData;
  protected visibleColumnIndicies: number[];

  @ViewChild('scrollContainer', { static: false }) scrollContainerRef: ElementRef<HTMLDivElement>;

  subRowTypeEnum = SubRowType;

  @Input()
  gridInstanceId: string;

  @Input()
  cellTemplate: TemplateRef<any>;

  @Input()
  headerTemplate: TemplateRef<any>;

  @Input()
  headerTemplateContext?: any;

  @Input()
  emptyTableText: { title: string, description: string };

  ngOnInit(): void {
    this.sortInfo$ = this.pagedGridFacade.getSortInfo(this.gridInstanceId);
    this.hostWidthChange$ = this.hostSizeChange().pipe(
      distinctUntilChanged(undefined, (resizeEntry) => resizeEntry.contentRect.width),
    );
    this.isHostVisible$ = this.hostWidthChange$.pipe(
      map((resizeEntry) => resizeEntry.contentRect.width > 5),
      skip(1),
      distinctUntilChanged(),
    );

    this.setupGridModelChangeHandling();
    this.setupHostResizeHandling();
    this.setupHostVisibilityChangeHandling();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.scrollContainerScrolled(this.elementRef.nativeElement.querySelector('.scrollable-container'));
    });
  }

  getGridTemplateColumnStyle(): string {
    if (this.gridModel.loading) {
      return `repeat(${this.gridModel.columnsData.length}, 1fr)`;
    }

    return this.gridModel.columnsData
      .map((columnData) => {
        if (columnData.minContentWidth) {
          return 'min-content';
        }

        if (Number.isInteger(columnData.widthPx)) {
          return `${columnData.widthPx}px`;
        }

        return 'minmax(max-content, auto)';
      })
      .join(' ');
  }

  onVsStart(pageInfo: IPageInfo): void {
    const startItem = this.gridModel.subRows[pageInfo.startIndex];
    const endItem = this.gridModel.subRows[pageInfo.endIndex];

    if (!startItem || !endItem) {
      return;
    }

    this.pagedGridFacade.setDisplayedRange(this.gridInstanceId, {
      startSubrowIndex: pageInfo.startIndex,
      startRowIndex: startItem.rowIndex,
      startChunkIndex: startItem.chunkIndex,
      endSubrowIndexIndex: pageInfo.endIndex,
      endRowIndex: endItem.rowIndex,
      endChunkIndex: endItem.chunkIndex,
    });
  }

  scrollContainerScrolled(scrollContainer: HTMLElement): void {
    this.calculateVisibleColumnsIndicies(scrollContainer);
  }

  showMoreClick(subRow: ExpandCollapseRow): void {
    this.pagedGridFacade.showMoreInRow(this.gridInstanceId, subRow.rowIndex, subRow.chunkIndex);
  }

  showLessClick(subRow: ExpandCollapseRow): void {
    this.pagedGridFacade.showLessInRow(this.gridInstanceId, subRow.rowIndex, subRow.chunkIndex);
  }

  showAllClick(subRow: ExpandCollapseRow): void {
    this.pagedGridFacade.showAllInRow(this.gridInstanceId, subRow.rowIndex, subRow.rowIndexInChunk, subRow.chunkIndex);
  }

  hideAllClick(subRow: ExpandCollapseRow): void {
    this.pagedGridFacade.hideAllInRow(this.gridInstanceId, subRow.rowIndex, subRow.rowIndexInChunk, subRow.chunkIndex);
  }

  headerClick(column: ColumnData): void {
    if (column.sortable) {
      // Ensure to be executed in ngZone, there was some error that Change sort ngrx action was dispatched from outside of ng zone, so no UI changes were observed.
      this.ngZone.run(() => {
        this.pagedGridFacade.changeSortColumn(this.gridInstanceId, column.columnId);
      });
      this.trackSortingColumn(column.columnId);
    }
  }

  protected getSubrowClasses(subRow: PagedGrid.BaseGridChunkSubRowMetadata): Record<string, boolean> {
    const subrowContext = this.getSubrowContext(subRow);

    return {
      'grid-subrow': true,
      [`grid-row-index-${ subRow.rowIndex }`]: true,
      'grid-even-row': subrowContext.evenRow,
      'grid-odd-row': subrowContext.oddRow,
      'grid-first-subrow-in-row': subrowContext.isFirstSubrowInRow,
      'grid-middle-subrow-in-row': subrowContext.isMiddleSubrowInRow,
      'grid-last-subrow-in-row': subrowContext.isLastSubrowInRow
    };
  }

  protected getSubrowContext(subRow: PagedGrid.BaseGridChunkSubRowMetadata): SubrowContext {
    return {
      index: subRow.index,
      rowId: subRow.rowId,
      rowIndex: subRow.rowIndex,
      subRowIndex: subRow.subRowIndex,
      evenRow: subRow.rowIndex % 2 === 0,
      oddRow: subRow.rowIndex % 2 !== 0,
      isFirstRow: subRow.index === 0,
      isLastRow: subRow.index === this.gridModel.subRows.length - 1,
      isFirstSubrowInRow: this.gridModel.subRows[subRow.index - 1]?.rowId !== subRow.rowId,
      isMiddleSubrowInRow:
        this.gridModel.subRows[subRow.index - 1]?.rowId === subRow.rowId &&
        this.gridModel.subRows[subRow.index + 1]?.rowId === subRow.rowId,
      isLastSubrowInRow: this.gridModel.subRows[subRow.index + 1]?.rowId !== subRow.rowId,
    };
  }

  protected getCellContext(subRow: PagedGrid.BaseGridChunkSubRowMetadata, columnIndex: number): CellContext {
    const currentColumnData = this.gridModel.columnsData[columnIndex];

    return {
      ...this.getSubrowContext(subRow),
      columnId: currentColumnData.columnId,
      isFirstCell: columnIndex === 0,
      isLastCell: columnIndex === this.gridModel.columnsData.length - 1,
      cellValue: subRow.subRowData ? subRow.subRowData[currentColumnData.columnId] : undefined,
      columnIndex
    };
  }

  /**
   * Creates sample invisible grid in DOM in order to calculate column width based on rendering content
   */
  private calculateTableData(): void {
    const renderingStartDate = new Date();

    delete this.calculatedTableData;
    this.sampleSubRows = this.getSampleRowsToRender();
    this.cd.detectChanges();

    const columnWidths = Array.from(
      this.elementRef.nativeElement.querySelectorAll(
        `.column-width-calculation-table > div:nth-child(-n + ${this.gridModel.columnsData.length})`,
      ),
    ).map((element) => {
      const rect = element.getBoundingClientRect();
      return {
        widthPx: rect.width,
        heightPx: rect.height,
        rect: rect,
      };
    });

    const calculatedColumnData: CalculatedColumnData[] = [];

    columnWidths.forEach((columnInfo, index) => {
      calculatedColumnData[index] = {
        widthPx: columnInfo.widthPx,
        heightPx: columnInfo.heightPx,
        rect: columnInfo.rect,
        positionPx: columnWidths
          .slice(0, index)
          .map((columnInfo2) => columnInfo2.widthPx)
          .reduce((prev, curr) => prev + curr, 0),
      } as any;
    });

    this.calculatedTableData = {
      columns: calculatedColumnData,
      widthPx: columnWidths.map((columnInfo) => columnInfo.widthPx).reduce((prev, curr) => prev + curr, 0),
    };
    delete this.sampleSubRows;
    this.cd.detectChanges();
    this.calculateVisibleColumnsIndicies(this.elementRef.nativeElement.querySelector('.scrollable-container'));
    this.cd.detectChanges();

    if (!this.gridModel.loading) {
      this.pagedGridAnalyticsFacade.trackRenderingTime(this.gridInstanceId, new Date().getTime() - renderingStartDate.getTime());
    }
  }

  private calculateVisibleColumnsIndicies(scrollContainerElement: HTMLElement): void {
    const visibleStart = scrollContainerElement.scrollLeft;
    const visibleEnd = scrollContainerElement.scrollLeft + scrollContainerElement.getBoundingClientRect().width;

    this.visibleColumnIndicies = [];
    for (let i = 0; i < this.calculatedTableData.columns.length; i++) {
      const current = this.calculatedTableData.columns[i];
      const currentStart = current.positionPx;
      const currentEnd = current.positionPx + current.widthPx;

      if (
        (currentStart > visibleStart && currentStart < visibleEnd) ||
        (currentEnd > visibleStart && currentEnd < visibleEnd) ||
        (currentStart < visibleStart && currentEnd > visibleEnd)
      ) {
        this.visibleColumnIndicies.push(i);
        this.cd.detectChanges();
      }
    }
  }

  private setupGridModelChangeHandling(): void {
    this.pagedGridFacade
      .getGrid(this.gridInstanceId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((gridModel) => {
        this.gridModel = gridModel;

        if (this.currentViewId !== gridModel.viewId) {
          this.calculateTableData();
          this.currentViewId = gridModel.viewId;
        } else {
          this.cd.detectChanges();
        }
      });
  }

  private setupHostResizeHandling(): void {
    this.isHostVisible$
      .pipe(
        switchMap((isVisible) => {
          if (isVisible) {
            return this.hostWidthChange$.pipe(debounceTime(500), skip(1));
          }

          return NEVER;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.calculateTableData());
  }

  private setupHostVisibilityChangeHandling(): void {
    this.isHostVisible$
      .pipe(
        filter((isVisible) => isVisible),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.calculateTableData();
      });
  }

  private hostSizeChange(): Observable<ResizeObserverEntry> {
    return new Observable<ResizeObserverEntry>((subject) => {
      const resizeObserver = new ResizeObserver((entries) => subject.next(entries[0]));
      resizeObserver.observe(this.elementRef.nativeElement);
      return () => resizeObserver.disconnect();
    }).pipe(shareReplay({ refCount: true, bufferSize: 1 }));
  }

  /**
   * Returns one subrow from grid when grid is in loading state.
   * Returns 50 subrows from grid when grid is not in loading state.
   */
  private getSampleRowsToRender(): PagedGrid.BaseGridChunkSubRowMetadata[] {
    return this.gridModel.subRows.slice(0, this.gridModel.loading ? 1: 50);
  }

  private async trackSortingColumn(columnName: string): Promise<void> {
    const { sortDirection } = await firstValueFrom(this.sortInfo$);
    this.pagedGridFacade.trackSortingColumnEvent(columnName, sortDirection);
  }
}
